import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useProductTour } from '@/store';

interface PlanificationFlowModalStore {
  isOpen: boolean;
  dontShowAgain: boolean;
  openModal: () => void;
  closeModal: () => void;
  setDontShowAgain: (value: boolean) => void;
}

const usePlanificationFlowModalStore = create<PlanificationFlowModalStore>()(
  persist(
    (set, get) => ({
      isOpen: false,
      dontShowAgain: false,
      openModal: () => {
        if (!get().dontShowAgain) set({ isOpen: true });
      },
      closeModal: () => set({ isOpen: false }),
      setDontShowAgain: (value: boolean) => set({ dontShowAgain: value })
    }),
    {
      name: 'planification-flow-modal',
      partialize: state => ({ dontShowAgain: state.dontShowAgain })
    }
  )
);

export const usePlanificationFlowModal = () => {
  const { isOpen, dontShowAgain, openModal, closeModal, setDontShowAgain } =
    usePlanificationFlowModalStore();
  const { unfinishedPageTour } = useProductTour();

  const [searchParams] = useSearchParams();
  const isPlanificationFlow = useMemo(
    () => searchParams.get('isPlanificationFlow') === 'true',
    [searchParams]
  );

  const shouldShowIntro = useMemo(
    () => searchParams.get('showPlanificationIntro') === 'true',
    [searchParams]
  );

  useEffect(() => {
    if (isPlanificationFlow && shouldShowIntro && !dontShowAgain && !unfinishedPageTour) {
      openModal();
      const params = new URLSearchParams(searchParams);
      params.delete('showPlanificationIntro');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [
    isPlanificationFlow,
    shouldShowIntro,
    dontShowAgain,
    unfinishedPageTour,
    openModal,
    searchParams
  ]);

  return {
    isPlanificationFlow,
    isOpen,
    dontShowAgain,
    openModal,
    closeModal,
    setDontShowAgain
  };
};
